/**
 * 管理员登录页
 *
 * 居中登录卡片，用户名 + 密码输入。
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  InputAdornment,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import LockIcon from '@mui/icons-material/Lock'
import { useAdminStore } from '@/store/useAdminStore'
import { adminLogin } from '@/api/services'
import SiteFooter from '@/components/SiteFooter'

export function LoginPage() {
  const navigate = useNavigate()
  const { setAuth } = useAdminStore()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleLogin = async () => {
    if (!username.trim()) {
      setError('请输入用户名')
      return
    }
    if (!password.trim()) {
      setError('请输入密码')
      return
    }

    setError('')
    setLoading(true)

    try {
      const res = await adminLogin(username.trim(), password.trim())
      setAuth(res)
      navigate('/dashboard', { replace: true })
    } catch (e) {
      const msg = e instanceof Error ? e.message : '登录失败，请重试'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin()
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #FF8C00 0%, #FFA333 100%)',
        p: 3,
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%', borderRadius: '16px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
        <CardContent sx={{ p: 4 }}>
          {/* Logo */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              component="img"
              src={`${import.meta.env.BASE_URL}logo.svg`}
              alt="研弈引力"
              sx={{ width: 56, height: 56, borderRadius: '14px', margin: '0 auto 12px' }}
            />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
              研弈引力服务商后台
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mt: 0.5 }}>
              餐饮品牌流量引擎管理平台
            </Typography>
          </Box>

          {/* 错误提示 */}
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
              {error}
            </Alert>
          )}

          {/* 登录表单 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="用户名"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: '#bbb', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="密码"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: '#bbb', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleLogin}
              disabled={loading}
              sx={{
                backgroundColor: '#FF8C00',
                borderRadius: '8px',
                py: 1.2,
                fontSize: '16px',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#E67E00' },
                '&:disabled': { backgroundColor: '#FFCC99' },
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : '登录'}
            </Button>
          </Box>

          {/* 初始密码提示（不泄露凭据） */}
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', color: '#bbb', mt: 2.5 }}
          >
            初始密码由部署时 ADMIN_INITIAL_PASSWORD 指定；忘记请联系运维重置
          </Typography>
        </CardContent>
      </Card>

      <SiteFooter />
    </Box>
  )
}

export default LoginPage
